<template>
    <DCard
        :title="__('settings.tenant_settings.form.fields.finance_systems.finance_system')"
        :subtitle="__('settings.tenant_settings.form.fields.finance_systems.finance_system_hint')"
        padding
        class="mb-3"
    >
        <DSelect
            id="finance_system"
            v-model="financeSystemsActive"
            :options="financeSystems"
            :placeholder="__(`settings.tenant_settings.form.fields.finance_systems.${financeSystemsActive}`)"
            @update:value="updateValue"
        />
    </DCard>
    <DCard
        :title="__(`settings.tenant_settings.form.fields.finance_systems.${financeSystemsActive}`)"
        padding
    >
        <DiamantForm
            v-if="isDiamantActive"
            :diamant-settings="diamantFinanceSystemSettings"
            :errors="errors"
            @form-submitted="submitData"
        />
        <NetvisorForm
            v-if="isNetvisorActive"
            :net-visor-settings="netvisorFinanceSystemSettings"
            :errors="errors"
            @form-submitted="submitData"
        />
        <AntagonistForm
            v-if="isAntagonistActive"
            :antagonist-settings="antagonistFinanceSystemSettings"
            :errors="errors"
            @form-submitted="submitData"
        />
        <PayEx360AndVismaBusinessForm
            v-if="isPayEx360AndVismaBusinessActive"
            :payex360-system-settings="payex360SystemSettings"
            :visma-business-system-settings="vismaBusinessSystemSettings"
            :errors="errors"
            @form-submitted="submitData"
        />
        <NetSuiteForm
            v-if="isNetSuiteActive"
            :net-suite-settings="netSuiteFinanceSystemSettings"
            :payment-reference-options="paymentReferenceOptions"
            :errors="errors"
            @form-submitted="submitData"
        />
    </DCard>

    <!-- NetSuite currency mapping (saved together with the NetSuite settings) -->
    <NetSuiteCurrencyMapping
        v-if="isNetSuiteActive"
        :initial-mappings="currencyMappings"
        :currency-code-options="currencyCodeOptions"
        :errors="errors"
        @update:mappings="onCurrencyMappingsChange"
    />

    <!-- Legacy finance system form  -->
    <DForm
        class="my-3"
        :errors="errors"
        @submit.prevent="submitFormLegacy"
    >
        <DCard
            :title="__('settings.tenant_settings.form.fields.finance_systems.legacy_finance_system')"
            :subtitle="__('settings.tenant_settings.form.fields.finance_systems.legacy_finance_system_hint')"
            padding
            class="mb-3"
        >
            <DSelect
                id="finance_system_legacy"
                v-model="formLegacy.legacy_finance_system"
                :options="legacyFinanceSystemOptions"
                :placeholder="__(`settings.tenant_settings.form.fields.finance_systems.${legacyFinanceSystemsActive}`)"
            />
            <DButton
                class="my-3"
                color="secondary"
                :label="__(`settings.tenant_settings.form.fields.finance_systems.legacy_finance_system_save`)"
                :disabled="!formLegacy.isDirty || formLegacy.processing"
                @click="submitFormLegacy"
            />
        </DCard>
    </DForm>

    <!-- Tax Code Mapping Component used just for NetSuite and invoice cancellations -->
    <TaxCodeMapping
        v-if="isNetSuiteActive"
        :initial-mappings="taxCodeMappings"
        :errors="errors"
    />
</template>

<script setup>
import { route } from '~@/Composables/route'
import { computed, ref } from 'vue'

import DiamantForm from '~@/Components/Settings/FinanceSystemSettings/DiamantForm'
import NetvisorForm from '~@/Components/Settings/FinanceSystemSettings/NetvisorForm'
import AntagonistForm from '~@/Components/Settings/FinanceSystemSettings/AntagonistForm'
import PayEx360AndVismaBusinessForm from '~@/Components/Settings/FinanceSystemSettings/PayEx360AndVismaBusinessForm'
import NetSuiteForm from '~@/Components/Settings/FinanceSystemSettings/NetSuiteForm.vue'
import TaxCodeMapping from '~@/Components/Settings/FinanceSystemSettings/TaxCodeMapping.vue'
import NetSuiteCurrencyMapping from '~@/Components/Settings/FinanceSystemSettings/NetSuiteCurrencyMapping.vue'
import { useForm } from '@inertiajs/vue3'

const props = defineProps({
    diamantFinanceSystemSettings: {
        type: Object,
        default () {
            return {}
        }
    },
    netvisorFinanceSystemSettings: {
        type: Object,
        default () {
            return {}
        }
    },
    antagonistFinanceSystemSettings: {
        type: Object,
        default () {
            return {}
        }
    },
    payex360SystemSettings: {
        type: Object,
        default () {
            return {}
        }
    },
    vismaBusinessSystemSettings: {
        type: Object,
        default () {
            return {}
        }
    },
    netSuiteFinanceSystemSettings: {
        type: Object,
        default () {
            return {}
        }
    },
    financeSystem: {
        type: String,
        default () {
            return ''
        }
    },
    legacyFinanceSystem: {
        type: String,
        default () {
            return ''
        }
    },
    financeSystems: {
        type: Array,
        default: () => { return [] }
    },
    taxCodeMappings: {
        type: Array,
        default: () => []
    },
    currencyMappings: {
        type: Array,
        default: () => []
    },
    currencyCodeOptions: {
        type: Array,
        default: () => []
    },
    paymentReferenceOptions: {
        type: Array,
        default: () => []
    },
    errors: {
        type: Object,
        default () {
            return {}
        }
    }
})

const financeSystemsActive = ref(props.financeSystem)

const isDiamantActive = computed(() => {
    return financeSystemsActive.value === 'diamant'
})
const isNetvisorActive = computed(() => {
    return financeSystemsActive.value === 'net_visor'
})
const isAntagonistActive = computed(() => {
    return financeSystemsActive.value === 'antagonist_finance'
})
const isPayEx360AndVismaBusinessActive = computed(() => {
    return financeSystemsActive.value === 'payex360'
})
const isNetSuiteActive = computed(() => {
    return financeSystemsActive.value === 'netsuite'
})
const currencyMappingRows = ref([])

const onCurrencyMappingsChange = (mappings) => {
    currencyMappingRows.value = mappings
}

const submitData = (form) => {
    if (isNetSuiteActive.value) {
        form.transform((data) => ({
            ...data,
            currency_mappings: currencyMappingRows.value
        }))
    }

    form.post(route('settings.finance-system-settings.store'))
}
const updateValue = (value) => {
    financeSystemsActive.value = value
}

const legacyFinanceSystemsActive = ref(props.legacyFinanceSystem)

const formLegacy = useForm({
    legacy_finance_system: props.legacyFinanceSystem,
})

const legacyFinanceSystemOptions = computed(() => [
    { label: '/', value: null },
    ...props.financeSystems.filter(
        (system) => system.value !== financeSystemsActive.value
    ),
])

function submitFormLegacy () {
    formLegacy.post(route('settings.finance-system-settings-legacy.store'))
}
</script>
